import { ChangeEvent, FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
// Models
import { RootState } from 'app/store';
// Redux
import { useAppSelector } from 'app/hooks/useStore';
// Selectors
import { selectCollectionsAsOptionsByFileType_TODO } from 'app/store/DMSCollections/DMSCollections.selectors';
// Mui
import { Box, Typography, FormHelperText, TextField, MenuItem, Divider } from '@mui/material';
// Components
import { Checkbox } from 'app/components/Mui';
// Utilities
import { isRequired } from 'app/utilities/Validations';
//
import useUploadContext, { IFileItem } from 'app/context/UploadFiles/useUploadContext';

type Props = {
  fileItem: IFileItem;
}

const FileItemCollectionSelector:FC<Props> = ({
  // Props
  fileItem
}) => {
  const { t } = useTranslation();

  // State
  const collectionOptions = useAppSelector((state:RootState) => selectCollectionsAsOptionsByFileType_TODO(state, {
    fileFormat: fileItem.fileFormat
  }));

  const { onUpdate, updateFileItemsCollections, formState:{ isSubmitted } } = useUploadContext();

  const [ isChecked, setIsChecked ] = useState(false);

  const handleChange = (event:ChangeEvent<HTMLInputElement>) => {
    const collectionId = Number(event.target.value);
    onUpdate(fileItem.id, { collectionId });

    if ( !isChecked ) return;

    updateFileItemsCollections(fileItem.fileFormat, collectionId);

    setIsChecked(false);
  }

  const handleClose = () => {
    if ( !isChecked || !fileItem.collectionId ) return;

    updateFileItemsCollections(fileItem.fileFormat, fileItem.collectionId);

    setIsChecked(false);
  }

  const handleCheckboxChange = (checked:boolean) => {
    setIsChecked(checked);
  }

  const errorText = isSubmitted && !fileItem.collectionId ? isRequired : '';

  if ( !collectionOptions ) return (
    <FormHelperText
      sx={{ alignSelf: 'center' }}
      error={true}
    >{t('dialogs.caseUploadDocuments.noSuitableCollection')}</FormHelperText>
  );
  return (
    <Box sx={{ position: 'relative' }}>
      <TextField
        sx={{ width: 200 }}
        label={t('labels.documentCollection')} value={(fileItem.collectionId || '').toString()}
        onChange={handleChange}
        required
        error={Boolean(errorText)}
        SelectProps={{
          onClose: handleClose,
          renderValue: (value) => {
            if ( !value ) return '';
            const foundedOption = collectionOptions.find((option) => option.id.toString() === value.toString());
            return foundedOption?.name || '';
          }
        }}
        select
        fullWidth
        margin="none"
        size="small"
      >
        {collectionOptions.map((option) => (
          <MenuItem
            key={`option item ${option.id}`}
            sx={{
              flexDirection: 'column',
              alignItems: 'flex-start'
            }}
            value={option.id.toString()}
          >
            <Typography variant="subtitle1">{option.name}</Typography>
            {option.props?.description ? (
              <Typography sx={{ maxWidth: 320, whiteSpace: 'normal' }} variant="caption">{option.props.description}</Typography>
            ) : null}
          </MenuItem>
        ))}
        <Divider />
        <Box sx={{
          py: 1.5,
          px: 4
        }}>
          <Checkbox
            name="applyToAll"
            label={
              <Fragment>
                <Typography>{t('dialogs.caseUploadDocuments.applyToAll')}</Typography>
                <FormHelperText sx={{ m: 0 }}>{t('dialogs.caseUploadDocuments.applyToAllHint')}</FormHelperText>
              </Fragment>
            }
            value={isChecked}
            onChange={(_, checked) => handleCheckboxChange(checked)}
            size="small"
          />
        </Box>
      </TextField>
      {errorText ? (
        <FormHelperText
          sx={{ position: 'absolute', bottom: 0, left: 0, mb: -6 }}
          error
        >{errorText}</FormHelperText>
      ) : null}
    </Box>
  )
}

export default FileItemCollectionSelector;
